import { useMutation } from "@apollo/client";
import { compact, flatMap } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AssessmentQuestionType,
  AssessmentState,
  AssessmentType,
  DeleteAssessmentAnswerMutationMutation,
  DeleteAssessmentAnswerMutationMutationVariables,
  GetAssessmentQuery,
  SaveAssessmentMutation,
  SaveAssessmentMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  errorNotificationVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Callout from "@components/callout/callout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import useDebounce from "@components/use-debounce/use-debounce";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import createOrUpdateAssessmentMutation from "../../graphql/create-or-update-assessment-mutation";
import deleteAssessmentAnswerMutation from "../../graphql/delete-assessment-answer-mutation";
import getMyAssessmentsQuery from "../../graphql/get-my-assessments-query";
import {
  assessmentTypeSectionHeadings,
  bgClassName,
  getAssessmentSections,
  getVisibleAssessmentQuestions,
  questionAppliesToReviewer,
} from "../../helpers";
import AssessmentDeliveryCalibrationChangelog from "../calibration/assessment-delivery-calibration-changelog";
import useAssessmentCalibrationData from "../calibration/use-assessment-calibration-data";
import AssessmentLayout from "../layouts/assessment-layout";
import AssessmentQuestionItem from "../questions/assessment-question-item";
import CoreValueBasedQuestions from "../questions/core-value-based-questions";
import GoalBasedQuestions from "../questions/goal-based-questions";
import {
  isCoreValueBasedQuestionType,
  isGoalBasedQuestionType,
  isRoleBasedQuestionType,
} from "../questions/helpers";
import { getQuestionClass } from "../questions/question";
import {
  AssessmentAnswer,
  AssessmentAnswerWithResponderAndId,
} from "../questions/types";
import AssessmentHeader from "./assessment-header";
import AssessmentOtherManagersCallout from "./assessment-other-managers-callout";
import AssessmentRoleBasedSection from "./assessment-role-based-section";
import AssessmentSectionSubjectVisibilityBanner from "./assessment-section-subject-visibility-banner";
import useChangedAnswers from "./use-changed-answers";

const AssessmentContent = ({
  myAssessmentsData,
  isLoadingAssessment,
  proposedAnswers,
  onChangeProposedAnswers,
  nextRoleVisibility,
  onChangeNextRoleVisibility,
}: {
  proposedAnswers: AssessmentAnswer[];
  onChangeProposedAnswers: (answers: AssessmentAnswer[]) => void;
  myAssessmentsData?: GetAssessmentQuery;
  isLoadingAssessment: boolean;
  nextRoleVisibility: Record<number, boolean>;
  onChangeNextRoleVisibility: (sectionId: number, show: boolean) => void;
}) => {
  const debouncedProposedAnswers = useDebounce(proposedAnswers, 1500);
  // The loader shows while this tab has changed answers to save. A failed save
  // turns it off, so the buttons work again, until the next edit.
  const lastSaveFailed = useRef(false);
  const pendingSaveRequest = useRef(false);
  const isFirstPageLoad = useRef(true);
  const {
    markChanged,
    forgetChanges,
    getChangedAnswers,
    hasChangedAnswers,
    markSaved,
  } = useChangedAnswers();
  const { assessmentId: assessmentIdParam } = useParams<{
    assessmentId: string;
  }>();
  const assessmentId = parseInt(assessmentIdParam);
  const organization = currentOrganizationVar();

  const link = useLink();
  const label = useLabel();
  const [showErrors, setShowErrors] = useState(false);

  const assessment = useMemo(
    () => (myAssessmentsData ? myAssessmentsData.assessment : null),
    [myAssessmentsData],
  );
  const assessmentTemplateId = useMemo(
    () => (assessment ? assessment.template.id : null),
    [assessment],
  );
  const complianceProgram = useMemo(
    () => assessment?.complianceProgram ?? null,
    [assessment],
  );

  const otherPeoplesAnswers: AssessmentAnswerWithResponderAndId[] = useMemo(
    () =>
      assessment?.otherParticipantsAnswers
        ? assertEdgesNonNull(assessment.otherParticipantsAnswers)
        : [],
    [assessment],
  );
  const mostRecentAnswers = useMemo(() => {
    return compact(assessment?.mostRecentAnswers);
  }, [assessment]);

  const isEngagementSurvey = useMemo(
    () =>
      assessment?.template.assessmentType === AssessmentType.EngagementSurvey,
    [assessment],
  );
  const target = useMemo(
    () =>
      !isEngagementSurvey && assessment
        ? assertNonNull(assessment.target)
        : null,
    [assessment, isEngagementSurvey],
  );
  const responder = useMemo(() => assessment?.responder ?? null, [assessment]);
  const isSelfAssessment = useMemo(
    // An anonymous survey has no subject and may hide its writer: two missing ids are not a self review.
    () => !!target && target.id === responder?.id,
    [responder, target],
  );

  const assessmentLabel = useMemo(() => {
    if (isEngagementSurvey) {
      return "Survey";
    }
    return "review";
  }, [isEngagementSurvey]);
  const assessmentPath = useMemo(() => {
    // Return to the Active tab after answering/submitting so the responder
    // lands back on their outstanding items (the default landing tab is "All").
    if (isEngagementSurvey) {
      return "/survey/active";
    }
    return "/assessments/active";
  }, [isEngagementSurvey]);

  const sectionNodes = useMemo(() => {
    if (!assessment) {
      return [];
    }

    return getAssessmentSections(
      assessment.template,
      assertNonNull(assessment.additionalQuestionSets),
    )
      .filter((section) =>
        section.questions.some(({ question }) =>
          questionAppliesToReviewer(question, isSelfAssessment),
        ),
      )
      .map((section) => ({
        ...section,
        questions: getVisibleAssessmentQuestions(
          section.questions,
          isSelfAssessment,
        ),
      }));
  }, [assessment, isSelfAssessment]);

  const questionNodes = useMemo(() => {
    if (!assessment) {
      return [];
    }
    return flatMap(sectionNodes, (section) => section.questions);
  }, [assessment, sectionNodes]);

  const { allCalibrations } = useAssessmentCalibrationData({
    complianceProgramId: complianceProgram?.id,
    templateId: assessmentTemplateId,
    targetId: target?.id,
  });

  const [deleteAnswers] = useMutation<
    DeleteAssessmentAnswerMutationMutation,
    DeleteAssessmentAnswerMutationMutationVariables
  >(deleteAssessmentAnswerMutation);

  const [saveAssessment, { loading: isSavingAssessment }] = useMutation<
    SaveAssessmentMutation,
    SaveAssessmentMutationVariables
  >(createOrUpdateAssessmentMutation);

  const handleSaveAssessment = useCallback(
    (
      answers: AssessmentAnswer[],
      state?: AssessmentState,
      refetchQueries?: any[],
      onCompleted?: (response: SaveAssessmentMutation) => void,
    ) => {
      saveAssessment({
        variables: {
          assessmentId: assertNonNull(assessmentId),
          organizationId: organization.id,
          targetId: isEngagementSurvey ? undefined : assertNonNull(target).id,
          assessmentTemplateId: assertNonNull(assessmentTemplateId),
          complianceProgramId: assertNonNull(complianceProgram).id,
          answers,
          state,
        },
        refetchQueries,
        onCompleted: (response) => {
          markSaved(answers);
          lastSaveFailed.current = false;
          if (onCompleted) {
            onCompleted(response);
          }
        },
        onError: () => {
          lastSaveFailed.current = true;
        },
      });
    },
    [
      assessmentId,
      assessmentTemplateId,
      complianceProgram,
      isEngagementSurvey,
      markSaved,
      organization.id,
      saveAssessment,
      target,
    ],
  );

  const handleSaveAsDraft = useCallback(() => {
    handleSaveAssessment(
      getChangedAnswers(proposedAnswers),
      AssessmentState.Draft,
      [],
      () => {
        const assessmentType = assertNonNull(
          assessment?.template.assessmentType,
        );
        const title =
          assessmentType === AssessmentType.EngagementSurvey
            ? "Survey saved for later"
            : `${getAssessmentTypeLabel(
                assessmentType,
                isSelfAssessment,
              )} ${label(assessmentLabel)} saved for later`;
        successNotificationVar({ title });
        link.redirect(assessmentPath);
      },
    );
  }, [
    assessment,
    assessmentLabel,
    assessmentPath,
    getChangedAnswers,
    handleSaveAssessment,
    isSelfAssessment,
    label,
    link,
    proposedAnswers,
  ]);

  // Autosave effect - queues save requests if one is already in progress.
  // It sends only the answers this tab changed, and nothing when there are none.
  useEffect(() => {
    if (
      !assessment ||
      debouncedProposedAnswers.length === 0 ||
      isFirstPageLoad.current ||
      assessment.state === AssessmentState.Submitted ||
      isSavingAssessment
    ) {
      if (isFirstPageLoad.current) {
        isFirstPageLoad.current = false;
      }
      if (isSavingAssessment) {
        pendingSaveRequest.current = true;
      }
      return;
    }
    const changedAnswers = getChangedAnswers(debouncedProposedAnswers);
    if (changedAnswers.length > 0) {
      handleSaveAssessment(changedAnswers);
    }
    // autosave, only fire to backend when debounced answers changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedProposedAnswers]);

  // Send another save request after save finishes if one is pending
  useEffect(() => {
    if (pendingSaveRequest.current) {
      pendingSaveRequest.current = false;
      const changedAnswers = getChangedAnswers(debouncedProposedAnswers);
      if (changedAnswers.length > 0) {
        handleSaveAssessment(changedAnswers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSavingAssessment]);

  const handleUpdateAnswer = useCallback(
    (newAnswer: AssessmentAnswer) => {
      lastSaveFailed.current = false;
      markChanged(
        proposedAnswers.find(
          (answer) => newAnswer.questionId === answer.questionId,
        ),
        newAnswer,
      );
      const newAnswers = [
        ...proposedAnswers.filter(
          (answer) => newAnswer.questionId !== answer.questionId,
        ),
        newAnswer,
      ];
      onChangeProposedAnswers(newAnswers);
    },
    [markChanged, onChangeProposedAnswers, proposedAnswers],
  );

  const handleDeleteAnswers = useCallback(
    (questionIds: number[]) => {
      if (assessmentId) {
        const newAnswers = proposedAnswers.filter(
          (answer) => !questionIds.includes(answer.questionId),
        );
        onChangeProposedAnswers(newAnswers);
        forgetChanges(questionIds);
        deleteAnswers({
          variables: {
            questionIds,
            assessmentId: assessmentId,
          },
        });
      }
    },
    [
      assessmentId,
      deleteAnswers,
      forgetChanges,
      onChangeProposedAnswers,
      proposedAnswers,
    ],
  );

  const areAllQuestionsAnswered = useMemo(() => {
    return questionNodes.every((node) => {
      const QuestionClass = getQuestionClass(node.question, { label });
      const answer = proposedAnswers.find(
        (answer) => answer.questionId === node.question.id,
      );
      return QuestionClass.isQuestionAnswered(answer);
    });
  }, [proposedAnswers, questionNodes, label]);

  const areAllCommentsEntered = useMemo(() => {
    return questionNodes.every((node) => {
      const QuestionClass = getQuestionClass(node.question, { label });
      const answer = proposedAnswers.find(
        (answer) => answer.questionId === node.question.id,
      );
      return QuestionClass.hasEnteredComment(answer);
    });
  }, [proposedAnswers, questionNodes, label]);

  const handleSubmitAssessment = useCallback(() => {
    if (!areAllCommentsEntered || !areAllQuestionsAnswered) {
      errorNotificationVar({
        title: "Please answer all questions and comments",
      });
      setShowErrors(true);
      return;
    }
    // The backend checks the required answers from the database, so Submit
    // also sends only the changed answers.
    handleSaveAssessment(
      getChangedAnswers(proposedAnswers),
      AssessmentState.Submitted,
      [getMyAssessmentsQuery],
      () => {
        const assessmentType = assertNonNull(
          assessment?.template.assessmentType,
        );
        const title =
          assessmentType === AssessmentType.EngagementSurvey
            ? "Survey submitted"
            : `${getAssessmentTypeLabel(
                assessmentType,
                isSelfAssessment,
              )} ${label(assessmentLabel)} submitted`;
        successNotificationVar({ title });
        link.redirect(assessmentPath);
      },
    );
  }, [
    areAllCommentsEntered,
    areAllQuestionsAnswered,
    getChangedAnswers,
    handleSaveAssessment,
    proposedAnswers,
    assessment,
    isSelfAssessment,
    label,
    assessmentLabel,
    link,
    assessmentPath,
  ]);

  if (isLoadingAssessment) {
    return (
      <Loading className={classNames(bgClassName, "p-6 w-full mx-auto")}>
        Loading
      </Loading>
    );
  }

  if (!assessment) {
    return (
      <div
        className={classNames(bgClassName, "flex-1 flex justify-center p-10")}
      >
        {label(assessmentLabel, { capitalize: true })} not found
      </div>
    );
  }

  // The API hides a review in draft from everyone but its admins, so its
  // participants get no program, and without it the answers cannot be saved
  // (TOPICFLOW-JS-20B).
  if (!complianceProgram) {
    return (
      <div
        className={classNames(bgClassName, "flex-1 flex justify-center p-10")}
      >
        This {label(assessmentLabel).toLowerCase()} is not open right now.
        Contact an Admin in your {label("organization")} if you need access.
      </div>
    );
  }

  const assessmentType = assessment.template.assessmentType;
  const canUpdate = assessment.canUpdate?.permission;
  const formDisabled =
    assessment.state === AssessmentState.Submitted || !canUpdate;
  const currentUser = currentUserVar();
  const currentUserIsResponder = responder && currentUser.id === responder.id;
  const isAdmin = isAdminVar();
  const showLoader =
    isSavingAssessment ||
    (!lastSaveFailed.current && hasChangedAnswers(proposedAnswers));

  return (
    <div>
      <AssessmentHeader myAssessmentsData={myAssessmentsData} />

      <div className="">
        <AssessmentLayout.AssessmentTypeSection>
          <AssessmentLayout.AssessmentTypeHeader>
            {
              assessmentTypeSectionHeadings(label, false, isSelfAssessment)[
                assessment.template.assessmentType
              ]
            }
          </AssessmentLayout.AssessmentTypeHeader>
          {assessment.state === AssessmentState.Submitted &&
            assessmentType === AssessmentType.Performance &&
            currentUserIsResponder &&
            !isAdmin &&
            !isSelfAssessment && (
              <Callout
                title={`To modify this ${label("review")}, contact an Admin`}
                theme="default"
                className="mt-4"
                icon="info"
              >
                Please contact an Admin in your {label("organization")} and ask
                them to "Reopen" this submission, if you would like to make any
                changes.
              </Callout>
            )}
          {assessment.state !== AssessmentState.Submitted &&
            currentUserIsResponder &&
            target && (
              <AssessmentOtherManagersCallout
                otherManagerReviewers={assessment.otherManagerReviewers}
                targetName={target.name}
              />
            )}
          <AssessmentDeliveryCalibrationChangelog
            assessmentTemplateId={assessmentTemplateId}
            calibration={complianceProgram?.calibration}
            complianceProgramId={complianceProgram?.id}
            targetId={target?.id}
          />
          <div className="mt-12">
            {sectionNodes.map((section) => {
              const sectionQuestions = section.questions;
              const heading =
                String(section.id).endsWith("-goals") ||
                String(section.id).endsWith("-role") ||
                String(section.id).endsWith(
                  "-core-values",
                ) ? null : section.title ? (
                  <AssessmentLayout.SectionHeading>
                    <AssessmentLayout.SectionHeadingTitle>
                      {section.title}
                    </AssessmentLayout.SectionHeadingTitle>
                    {section.description &&
                      !isEmptyValue(section.description) && (
                        <AssessmentLayout.SectionHeadingDescription>
                          <TextareaWysiwyg
                            editable={false}
                            className="bg-white w-full max-w-none"
                            value={section.description}
                          />
                        </AssessmentLayout.SectionHeadingDescription>
                      )}
                    {target &&
                      target?.id !== currentUser.id &&
                      !isEngagementSurvey && (
                        <AssessmentSectionSubjectVisibilityBanner
                          subjectVisibility={section.subjectVisibility}
                          className="mt-2"
                        />
                      )}
                  </AssessmentLayout.SectionHeading>
                ) : null;

              const regularQuestions = sectionQuestions.filter(
                ({ question }) =>
                  !isRoleBasedQuestionType(question.questionType) &&
                  !isGoalBasedQuestionType(question.questionType) &&
                  !isCoreValueBasedQuestionType(question.questionType),
              );

              const goalBasedQuestions = sectionQuestions.filter(
                ({ question }) =>
                  isGoalBasedQuestionType(question.questionType),
              );
              const overallGoalBasedQuestion = goalBasedQuestions.find(
                ({ question }) =>
                  question.questionType === AssessmentQuestionType.OverallGoal,
              );
              const individualGoalBasedQuestions = goalBasedQuestions.filter(
                ({ question }) =>
                  question.questionType ===
                  AssessmentQuestionType.IndividualGoal,
              );
              const coreValueBasedQuestions = sectionQuestions.filter(
                ({ question }) =>
                  isCoreValueBasedQuestionType(question.questionType),
              );
              const overallCoreValueBasedQuestion =
                coreValueBasedQuestions.find(
                  ({ question }) =>
                    question.questionType ===
                    AssessmentQuestionType.OverallRecognitionCoreValue,
                );
              const individualCoreValueBasedQuestions =
                coreValueBasedQuestions.filter(
                  ({ question }) =>
                    question.questionType ===
                    AssessmentQuestionType.IndividualRecognitionCoreValue,
                );

              return (
                <AssessmentLayout.SectionContainer key={section.id}>
                  {heading}
                  {regularQuestions.length !== 0 && (
                    <AssessmentLayout.SectionRegularQuestions>
                      {regularQuestions.map((questionWithWeight) => {
                        const { question } = questionWithWeight;
                        const answer = proposedAnswers.find(
                          (answer) => answer.questionId === question.id,
                        );
                        const mostRecentAnswer = mostRecentAnswers.find(
                          (answer) => answer.questionId === question.id,
                        );
                        const calibrations = allCalibrations.filter(
                          (c) =>
                            c.isPrecalibration && c.question.id === question.id,
                        );
                        return (
                          <AssessmentQuestionItem
                            key={question.id}
                            assessmentType={assessmentType}
                            answer={answer}
                            calibrations={calibrations}
                            mostRecentAnswer={mostRecentAnswer}
                            otherParticipantsAnswers={otherPeoplesAnswers.filter(
                              (answer) => answer.questionId === question.id,
                            )}
                            question={question}
                            onUpdateAnswer={handleUpdateAnswer}
                            formDisabled={formDisabled}
                            showError={showErrors}
                            target={target}
                          />
                        );
                      })}
                    </AssessmentLayout.SectionRegularQuestions>
                  )}

                  <AssessmentRoleBasedSection
                    assessmentType={assessmentType}
                    sectionQuestions={sectionQuestions}
                    isShowingNextRoleQuestions={
                      !!nextRoleVisibility[section.id]
                    }
                    onChangeShowingNextRoleQuestions={(show) =>
                      onChangeNextRoleVisibility(section.id, show)
                    }
                    subjectVisibility={section.subjectVisibility}
                    targetCurrentRoleIds={compact(
                      assessment.targetCurrentRoleIds,
                    )}
                    onUpdateAnswer={handleUpdateAnswer}
                    onDeleteAnswers={handleDeleteAnswers}
                    formDisabled={formDisabled}
                    showErrors={showErrors}
                    allAnswers={proposedAnswers}
                    otherParticipantsAnswers={otherPeoplesAnswers}
                    nextRoleAssessmentEnabled={
                      (!isSelfAssessment &&
                        !!myAssessmentsData?.assessment?.template
                          .roleBasedAssessmentsAssessNextRole) ||
                      (isSelfAssessment &&
                        !!myAssessmentsData?.assessment?.template
                          .roleBasedAssessmentsSelfAssessNextRole)
                    }
                    isSelfAssessment={isSelfAssessment}
                    target={target}
                  />

                  {String(section.id).endsWith("-goals") && (
                    <GoalBasedQuestions
                      assessmentType={assessmentType}
                      overallGoalBasedQuestion={overallGoalBasedQuestion}
                      individualGoalBasedQuestions={
                        individualGoalBasedQuestions
                      }
                      subjectVisibility={section.subjectVisibility}
                      proposedAnswers={proposedAnswers}
                      formDisabled={formDisabled}
                      showErrors={showErrors}
                      otherParticipantsAnswers={otherPeoplesAnswers}
                      onUpdateAnswer={handleUpdateAnswer}
                      target={target}
                    />
                  )}
                  {String(section.id).endsWith("-core-values") && (
                    <CoreValueBasedQuestions
                      assessmentType={assessmentType}
                      overallCoreValueBasedQuestion={
                        overallCoreValueBasedQuestion
                      }
                      individualCoreValueBasedQuestions={
                        individualCoreValueBasedQuestions
                      }
                      subjectVisibility={section.subjectVisibility}
                      proposedAnswers={proposedAnswers}
                      formDisabled={formDisabled}
                      showErrors={showErrors}
                      target={target}
                      otherParticipantsAnswers={otherPeoplesAnswers}
                      onUpdateAnswer={handleUpdateAnswer}
                      periodStartDate={complianceProgram?.periodStartDate}
                      periodEndDate={complianceProgram?.periodEndDate}
                    />
                  )}
                </AssessmentLayout.SectionContainer>
              );
            })}

            <div className="flex items-center justify-end mt-4 pb-6">
              {!formDisabled && (
                <div className="flex items-center gap-2">
                  {showLoader && <Loading mini size="4" />}
                  <Button
                    onClick={handleSaveAsDraft}
                    theme={buttonTheme.text}
                    disabled={showLoader}
                    text="Save as draft"
                  />
                  <Button
                    disabled={showLoader}
                    type="button"
                    theme={buttonTheme.primary}
                    onClick={handleSubmitAssessment}
                    text="Submit"
                  />
                </div>
              )}
            </div>
          </div>
        </AssessmentLayout.AssessmentTypeSection>
      </div>
    </div>
  );
};

export default AssessmentContent;
